/**
 * APScreenTitle Component
 *
 * A comprehensive screen title component following Figma design system specifications.
 * Supports back navigation, breadcrumbs, title, and description text.
 *
 * @example
 * ```tsx
 * <APScreenTitle
 *   title="Finance Overview"
 *   description="Review your balance activity or export reports."
 *   showArrow={true}
 *   showDescription={true}
 *   showBreadcrumb={true}
 *   breadcrumbs={[
 *     { text: 'Home', href: '/', isClickable: true },
 *     { text: 'Finance', isCurrent: true }
 *   ]}
 *   onBackClick={() => navigate(-1)}
 * />
 * ```
 */

import { useCallback, useMemo } from 'react';

import type { IAPScreenTitleProps } from './types';

import './styles.scss';

import { useNavigate } from '@tanstack/react-router';

import { APTypography } from '@shared/components/atoms';
// Import shared components
import APIconButton from '@shared/components/atoms/icon-button';
import APBreadcrumbs from '@shared/components/molecules/breadcrumbs';
import { useTranslate } from '@shared/hooks/use-translate';

/**
 * APScreenTitle - Production-ready screen title component
 *
 * Follows AstroPay design system and accessibility guidelines.
 */
export const APScreenTitle: React.FC<IAPScreenTitleProps> = ({
  title,
  description,
  showArrow = true,
  showDescription = true,
  showBreadcrumb = false,
  breadcrumbs = [],
  onBackClick,
  onBreadcrumbClick,
  backTo,
  className = '',
  'data-testid': testId,
}) => {
  const translate = useTranslate();
  // Calculate component size based on visible elements
  const sizeVariant = useMemo(() => {
    if (showBreadcrumb && showDescription) return 'full';
    if (showBreadcrumb) return 'with-breadcrumb';
    if (showDescription) return 'with-description';
    return 'default';
  }, [showBreadcrumb, showDescription]);
  const navigate = useNavigate();

  const handleBackClick = useCallback(() => {
    if (backTo) {
      navigate({ to: backTo });
    } else {
      onBackClick?.();
    }
  }, [backTo, onBackClick, navigate]);

  // Construct class names using BEM methodology
  const containerClasses = ['ap-screen-title', `ap-screen-title--size-${sizeVariant}`, className]
    .filter(Boolean)
    .join(' ');

  const contentClasses = [
    'ap-screen-title__content',
    showArrow ? 'ap-screen-title__content--with-arrow' : 'ap-screen-title__content--without-arrow',
    showBreadcrumb && 'ap-screen-title__content--with-breadcrumb',
    showDescription
      ? 'ap-screen-title__content--with-description'
      : 'ap-screen-title__content--without-description',
  ]
    .filter(Boolean)
    .join(' ');

  const backButtonClasses = [
    'ap-screen-title__back-button',
    showBreadcrumb
      ? 'ap-screen-title__back-button--with-breadcrumb'
      : 'ap-screen-title__back-button--default',
  ]
    .filter(Boolean)
    .join(' ');

  const breadcrumbsClasses = [
    'ap-screen-title__breadcrumbs',
    showArrow && 'ap-screen-title__breadcrumbs--with-arrow',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses} data-testid={testId}>
      {/* Back arrow button */}
      {showArrow && (
        <div className={backButtonClasses}>
          <APIconButton
            icon="arrow_left_line"
            variant="transparent"
            size="large"
            onClick={handleBackClick}
            aria-label={translate('aria.go-back')}
            data-testid={testId ? `${testId}-back-button` : undefined}
          />
        </div>
      )}

      {/* Breadcrumbs */}
      {showBreadcrumb && breadcrumbs.length > 0 && (
        <div className={breadcrumbsClasses}>
          <APBreadcrumbs
            items={breadcrumbs}
            onItemClick={onBreadcrumbClick}
            data-testid={testId ? `${testId}-breadcrumbs` : undefined}
          />
        </div>
      )}

      {/* Main content */}
      <div className={contentClasses}>
        {/* Title */}
        <APTypography
          as="h1"
          size="large"
          weight="600"
          className="ap-screen-title__title"
          data-testid={testId ? `${testId}-title` : undefined}
        >
          {title}
        </APTypography>
        {/* Description */}
        {showDescription && description && (
          <APTypography
            as="p"
            size="base"
            weight="400"
            className="ap-screen-title__description"
            data-testid={testId ? `${testId}-description` : undefined}
          >
            {description}
          </APTypography>
        )}
      </div>
    </div>
  );
};

APScreenTitle.displayName = 'APScreenTitle';

export type { IAPScreenTitleProps };
export default APScreenTitle;
